import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import dayjs from 'utils/dayjs';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';

import { useParams } from 'react-router-dom';
import { CurrencyFilters } from 'components/CurrencyFilters';

import { fetchExchangersList } from 'actions';
import {
  analyticsEventPost,
  analyticsEventsPostBatch,
} from 'actions/analytics';

import {
  DEFAULT_SOURCE_CURRENCY,
  getHrefExchanger,
  getHrefAllExchangers,
  getCityIdBySlug,
  parseDate,
} from 'utils';

import { currencies } from 'constants/currency';

import Icon from 'components/commons/Icon';
import { Link } from 'components/CurrencyTable/styles';
import { branchColor } from 'utils/subways';
import {
  colorGrey400,
  colorPrimary,
  colorAccent100,
  colorSecondary,
} from 'styles';
import {
  Wrapper,
  Header,
  Title,
  TableTitleContent,
  TableTitle,
  ExchangersList,
  Row,
  CurrContent,
  Update,
  TableHead,
  Bottom,
  Column,
  ColumnUpdate,
  ExchangerLink,
  IconWatch,
  Amount,
  Rates,
  ColumnAddress,
} from './styles';

// Helper function to get metro icon color based on metroId
const getMetroIconColor = metroId => {
  const colorName = branchColor(metroId);
  const colorMap = {
    green: colorPrimary,
    blue: colorAccent100,
    red: colorSecondary,
    grey: colorGrey400,
  };
  return colorMap[colorName] || colorGrey400;
};

const currencyOptions = [
  {
    id: 'USD',
    IconWatch,
  },
  {
    id: 'EUR',
  },
  {
    id: 'PLN',
  },
];

const rowsTitle = [
  {
    id: 1,
    name: 'actions',
    align: 'left',
  },

  {
    id: 2,
    name: 'address',
    align: 'left',
  },
  {
    id: 3,
    name: 'publish',
    align: 'left',
  },
  {
    id: 4,
    name: null,
    align: 'left',
  },
];

// Helper функція для форматування суми
const formatAmount = (amount, formatNumber) => {
  if (!amount) return amount;
  return formatNumber(Number(amount), {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const renderRateContent = (
  // eslint-disable-next-line react/prop-types
  { buyRate, sellRate, currency, minSellAmount, maxSellAmount },
  { formatMessage, formatNumber }
) => {
  const bid = buyRate
    ? Number(buyRate)
        .toFixed(2)
        .replace('.', ',')
    : '-';
  const ask = sellRate
    ? Number(sellRate)
        .toFixed(2)
        .replace('.', ',')
    : '-';

  let amountText = null;

  if (minSellAmount && maxSellAmount) {
    amountText = formatMessage(
      { id: 'table.exchanger.amount.fromTo' },
      {
        minAmount: formatAmount(minSellAmount, formatNumber),
        maxAmount: formatAmount(maxSellAmount, formatNumber),
        currency,
      }
    );
  } else if (maxSellAmount) {
    amountText = formatMessage(
      { id: 'table.exchanger.amount.to' },
      {
        maxAmount: formatAmount(maxSellAmount, formatNumber),
        currency,
      }
    );
  } else if (minSellAmount) {
    amountText = formatMessage(
      { id: 'table.exchanger.amount.from' },
      {
        minAmount: formatAmount(minSellAmount, formatNumber),
        currency,
      }
    );
  }

  return (
    <Rates>
      <CurrContent>{`${bid} / ${ask}`}</CurrContent>
      <Amount>{amountText && <div>{amountText}</div>}</Amount>
    </Rates>
  );
};

const ExchangersTable = ({ currencyList, exchangersList }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage, formatNumber, locale } = intl;
  const { city, currency } = useParams();

  const [currentOption, setCurrentOption] = useState(
    currency?.toUpperCase() || DEFAULT_SOURCE_CURRENCY.toUpperCase()
  );

  // Відстежуємо, чи була валюта змінена через onChangeFilter()
  const isCurrencyChangedByFilter = useRef(false);
  // Відстежуємо чи вже відправляли аналітику для цього блоку
  const hasTrackedVisibility = useRef(false);

  const citiesEntities = useSelector(
    state => state.currency?.citiesList?.entities || {}
  );

  const onChangeFilter = id => {
    isCurrencyChangedByFilter.current = true;

    const cityId = city ? getCityIdBySlug(citiesEntities, city) : null;
    if (cityId) {
      dispatch(
        fetchExchangersList({
          intl,
          currency: id.toLowerCase(),
          city: cityId,
        })
      );
    }
    setCurrentOption(id);
  };

  useEffect(() => {
    // Скидаємо флаг при зміні city або currency через useParams
    isCurrencyChangedByFilter.current = false;
    // Скидаємо флаг трекінгу при зміні city або currency
    hasTrackedVisibility.current = false;

    // Отримуємо city ID за slug
    const cityId = city ? getCityIdBySlug(citiesEntities, city) : null;

    if (cityId) {
      dispatch(
        fetchExchangersList({
          intl,
          currency: currency || currencies.USD,
          city: cityId,
        })
      );
    }
  }, [city, currency, citiesEntities]);

  // Рендеримо компонент тільки якщо є city
  if (!city) {
    return null;
  }

  const isLoading = exchangersList?.isFetching;
  const isInitialized = exchangersList?.isInitialized;
  const hasItems = exchangersList?.items?.length > 0;

  if (!isCurrencyChangedByFilter.current && isInitialized && !hasItems) {
    return null;
  }

  return (
    <Wrapper>
      <Header>
        <Title>
          <FormattedMessage id="table.exchangers.mainTitle" />
        </Title>
        <CurrencyFilters
          primaryOptions={currencyOptions}
          onChange={onChangeFilter}
          value={currentOption}
          optionsList={currencyList}
        />
      </Header>

      {hasItems && (
        <VisibilitySensor
          onChange={isVisible => {
            if (
              isVisible &&
              !hasTrackedVisibility.current &&
              exchangersList?.items?.length
            ) {
              hasTrackedVisibility.current = true;

              const events = exchangersList.items
                .filter(
                  exchanger =>
                    exchanger.id && exchanger.buyRate && exchanger.currency
                )
                .map(exchanger => ({
                  profileId: exchanger.id,
                  objectId: exchanger.id,
                  rate: exchanger.buyRate,
                  currency: exchanger.currency.toLowerCase(),
                }));

              if (events.length > 0) {
                const cacheKey = `${city}-${currency}-${exchangersList.items.length}`;
                analyticsEventsPostBatch(events, cacheKey);
              }
            }
          }}
          partialVisibility
          minTopValue={100}
        >
          <ExchangersList>
            <TableHead>
              <Row>
                {rowsTitle.map(el => (
                  <TableTitle key={el.id}>
                    <TableTitleContent align={el.align} id={el.name}>
                      {el.name
                        ? formatMessage({
                            id: `tableHeader.${el.name}`,
                          })
                        : ''}
                    </TableTitleContent>
                  </TableTitle>
                ))}
              </Row>
            </TableHead>

            <tbody>
              {exchangersList?.items.map(
                ({
                  updated,
                  id,
                  buyRate,
                  sellRate,
                  address,
                  minSellAmount,
                  maxSellAmount,
                  currency,
                  city,
                  citySlug,
                  metro,
                  metroId,
                  ownerId,
                }) => {
                  const href = getHrefExchanger({ id, citySlug, locale });
                  const isClickable = Boolean(id && buyRate && currency);

                  const handleClick = () => {
                    if (!isClickable) {
                      return;
                    }

                    const trackEvent = analyticsEventPost({
                      profileId: ownerId,
                      objectId: id,
                      type: 'on_route',
                      rate: buyRate,
                      currency: currency.toLowerCase(),
                    });
                    trackEvent();

                    window.location.href = href;
                  };

                  const handleKeyDown = event => {
                    if (!isClickable) {
                      return;
                    }

                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleClick();
                    }
                  };

                  return (
                    <React.Fragment key={id}>
                      <Row
                        $clickable={isClickable}
                        onClick={isClickable ? handleClick : undefined}
                        onKeyDown={isClickable ? handleKeyDown : undefined}
                        role={isClickable ? 'link' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                      >
                        <Column>
                          {renderRateContent(
                            {
                              buyRate,
                              sellRate,
                              currency,
                              minSellAmount,
                              maxSellAmount,
                            },
                            { formatMessage, formatNumber }
                          )}
                        </Column>

                        <ColumnAddress>
                          {metro && (
                            <Icon
                              name="metroIcon"
                              fill={getMetroIconColor(metroId)}
                              style={{ marginRight: '4px', marginTop: '-3px' }}
                            />
                          )}
                          {`${metro ? `${metro} • ` : ''}${city}, ${address}`}
                        </ColumnAddress>

                        <ColumnUpdate>
                          <Update>
                            {(() => {
                              const updatedDate = parseDate(updated);

                              if (!updatedDate) {
                                return '—';
                              }

                              const localizedDate = updatedDate.locale(locale);
                              const isToday = localizedDate.isSame(
                                dayjs(),
                                'day'
                              );

                              if (isToday) {
                                return `${formatMessage({
                                  id: 'table.exchanger.updated.today',
                                })}, ${localizedDate.format('HH:mm')}`;
                              }

                              return localizedDate.format('DD MMMM HH:mm');
                            })()}
                          </Update>
                        </ColumnUpdate>

                        <Column>
                          <ExchangerLink as="span">
                            <Icon name="vectorStrokeMb" />
                          </ExchangerLink>
                        </Column>
                      </Row>
                    </React.Fragment>
                  );
                }
              )}
            </tbody>
          </ExchangersList>
        </VisibilitySensor>
      )}

      {!isLoading && isInitialized && !hasItems && (
        <FormattedMessage id="noData.noFound" />
      )}

      {hasItems && (
        <Bottom>
          <Link
            href={getHrefAllExchangers({
              locale,
              currency,
              city,
            })}
          >
            <FormattedMessage id="table.exchangers.link" />
            <Icon name="vectorStrokeMb" style={{ marginLeft: '10px' }} />
          </Link>
        </Bottom>
      )}
    </Wrapper>
  );
};

const mapStateToProps = state => ({
  currencyList: state.currency?.currencyList || [],
  exchangersList: state.exchangers.list || {},
});

export default connect(mapStateToProps)(injectIntl(ExchangersTable));

ExchangersTable.propTypes = {
  exchangersList: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        buyRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sellRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        currency: PropTypes.string,
        city: PropTypes.string,
        citySlug: PropTypes.string,
        address: PropTypes.string,
        metro: PropTypes.string,
        metroId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        updated: PropTypes.string,
        minSellAmount: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        maxSellAmount: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
      })
    ),
    isFetching: PropTypes.bool,
    isInitialized: PropTypes.bool,
    isRequestFailed: PropTypes.bool,
  }),
  currencyList: PropTypes.arrayOf(PropTypes.shape({})),
};

ExchangersTable.defaultProps = {
  currencyList: [],
  exchangersList: {},
};
